'use client'

/**
 * Site-wide "hide leveraged / inverse ETFs" preference — shared by every ticker panel
 * (Feeds, Trending, Movers, …) so toggling it in one place applies everywhere. Default ON
 * (hidden): 2×/3× daily-reset ETFs amplify their underlying and churn alerts, crowding out
 * real standouts. The user can flip it on per-device (persisted in localStorage).
 */

import { useCallback, useEffect, useState } from 'react'
import { nameLooksLeveragedOrInverse, KNOWN_LEVERAGED_INVERSE_SYMBOLS } from '@/lib/watcher/leveragedEtf'

const KEY = 'dt_hide_lev_etf'

export const LEV_TOOLTIP =
  'Leveraged / inverse ETF — a 2×/3× daily-reset product that amplifies its underlying’s move and decays over time. The big % swings here are mechanical leverage, not a standalone company.'

/** Detect a leveraged/inverse ETF from symbol + optional name (name catches brand-new tickers). */
export function isLeveragedEtf(symbol: string, name?: string | null): boolean {
  return nameLooksLeveragedOrInverse(name) || KNOWN_LEVERAGED_INVERSE_SYMBOLS.has(String(symbol).toUpperCase())
}

/** Shared hide-leveraged-ETFs toggle. Returns { hide, toggle }; default hide = true. */
export function useHideLeveragedEtf(): { hide: boolean; toggle: () => void } {
  const [hide, setHide] = useState(true)
  useEffect(() => {
    try { const v = localStorage.getItem(KEY); if (v != null) setHide(v === '1') } catch { /* SSR / no storage */ }
    // Keep panels in sync if the toggle is flipped in another tab/panel.
    const onStorage = (e: StorageEvent) => { if (e.key === KEY && e.newValue != null) setHide(e.newValue === '1') }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])
  const toggle = useCallback(() => setHide((h) => {
    const n = !h
    try { localStorage.setItem(KEY, n ? '1' : '0') } catch { /* noop */ }
    return n
  }), [])
  return { hide, toggle }
}
